
import React, { useEffect, useState } from "react";
import classnames from "classnames";

// reactstrap components
import {
    UncontrolledAlert,
    Button,
    Card,
    CardHeader,
    Table,
    Container,
    Row,
    Col,
    UncontrolledTooltip,
    Modal,
    Form,
    FormGroup,
    InputGroup,
    InputGroupAddon,
    Input,
    InputGroupText,
    ModalBody,
    ModalHeader,
    Label,
    Pagination, PaginationItem, PaginationLink,
} from "reactstrap";
// core components
import SimpleHeader from "components/Headers/SimpleHeader.js";
import { getAllCategories, deleteCategory, updateCategory } from "api/categoryApiCall";
import { useNavigate } from 'react-router-dom';
import Swal from "sweetalert2";
import { useTranslation } from '../../../i18n/context';

function Categories() {
    const [allCategories, setAllCategories] = useState([]);
    const [filteredCategories, setFilteredCategories] = useState([]);
    const [allCategoriesError, setAllCategoriesError] = useState({});
    const [formModal, setFormModal] = useState(false);
    const [categoryName, setCategoryName] = useState(false);
    const [showSuccessAlert, setShowSuccessAlert] = useState(false);
    const [showErrorAlert, setShowErrorAlert] = useState(false);
    const [errorMessages, setErrorMessages] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState({});
    const [tooltipIds, setTooltipIds] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const navigate = useNavigate();
    const { t } = useTranslation();

    useEffect(() => {
        if (Array.isArray(allCategories)) {
            const ids = allCategories.map((_, index) => ({
                edit: `tooltipEdit${index}`,
                delete: `tooltipDelete${index}`,
            }));
            setTooltipIds(ids);
        } else {
            console.warn('allCategories is not an array:', allCategories);
        }
    }, [allCategories]);

    useEffect(() => {
        const results = allCategories.filter(category =>
            category.category_name.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredCategories(results);
        setCurrentPage(1);
    }, [searchTerm, allCategories]);


    const toggleModal = () => {
        setFormModal(!formModal);
    };

    const handleEditClick = (categoryData) => {
        setSelectedCategory(categoryData);
        toggleModal();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const [errorCategoryCall, successUserCall] = await updateCategory(selectedCategory);
            if (errorCategoryCall) {
                setErrorMessages(errorCategoryCall.message);
                setShowErrorAlert(true);
            } else {
                setShowSuccessAlert(true);
                window.location.reload();
            }
        } catch (error) {
            setErrorMessages([error.message]);
            setShowErrorAlert(true);
        }
    };

    const handleDeleteClick = (categoryId) => {
        Swal.fire({
            title: t('category.sure'),
            text: t('category.delete_text'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: t('category.delete_confirm')
        }).then((result) => {
            if (result.isConfirmed) {
                deleteCategory(categoryId)
                    .then(([error, successData]) => {
                        if (error) {
                            Swal.fire(t('common.error'), error.message || t('category.not_successful'), 'error');
                        } else {
                            Swal.fire(t('profile.delete_success_title'), t('profile.success')).then(() => {
                                window.location.reload();
                            });
                        }
                    })
                    .catch(error => {
                        Swal.fire(t('category.delete_error_title'), 'error');
                    });
            }
        });
    };

    const handleCategoriesCall = async () => {
        const [errorCategoriesCall, successCategoryCall] = await getAllCategories();
        if (errorCategoriesCall) {
            setAllCategoriesError(errorCategoriesCall);
        } else if (successCategoryCall) {
            setAllCategories(successCategoryCall);
        } else {
            setAllCategoriesError({ message: 'No data received from the server' });
        }
    };

    useEffect(() => {
        handleCategoriesCall();
    }, []);

    useEffect(() => {
        const timeOutError = setTimeout(() => {
            setAllCategoriesError({});
        }, 10000);
        return () => {
            clearTimeout(timeOutError);
        };
    }, [allCategoriesError]);

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
    };

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredCategories.slice(indexOfFirstItem, indexOfLastItem);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    return (
        <>
            <SimpleHeader name={t('category.cat')} parentName={t('category.cat')} />
            <Container className="mt--6" fluid>
                <Card>
                    <CardHeader className="border-0">
                        <Row>
                            <Col xs="6">
                                <h3 className="mb-0">{t('category.management')}</h3>
                            </Col>
                            <Col className="text-right" xs="6">
                                <Button
                                    className="btn-round btn-icon"
                                    color="primary"
                                    id="tooltip443412080"
                                    onClick={() => navigate('/admin/addcategories')}
                                    size="sm"
                                >
                                    <span className="btn-inner--icon mr-1">
                                        <i className="fas fa-user-edit" />
                                    </span>
                                    <span className="btn-inner--text">{t('category.add_category')}</span>
                                </Button>
                                <UncontrolledTooltip delay={0} target="tooltip443412080">
                                    {t('category.add_category')}
                                </UncontrolledTooltip>
                            </Col>
                        </Row>
                    </CardHeader>

                    <div className="px-3 py-2">
                        <Input
                            type="text"
                            placeholder={t('category.search')}
                            value={searchTerm}
                            onChange={handleSearchChange}
                        />
                    </div>

                    <div className="table-responsive">
                        <Table className="align-items-center table-flush">
                            <thead className="thead-light">
                                <tr>
                                    <th className="sort" data-sort="name" scope="col">
                                        {t('category.cat')}
                                    </th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody className="list">
                                {currentItems.map((categoryData, index) => (
                                    <tr key={index}>
                                        <td>{categoryData?.category_name}</td>
                                        <td className="table-actions">
                                            <a
                                                className="table-action"
                                                href="#pablo"
                                                id={tooltipIds[index]?.edit}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    handleEditClick(categoryData);
                                                }}
                                            >
                                                <i className="fas fa-user-edit" />
                                            </a>
                                            {tooltipIds[index] && (
                                                <UncontrolledTooltip delay={0} target={tooltipIds[index].edit}>
                                                    {t('category.edit_category')}
                                                </UncontrolledTooltip>
                                            )}
                                            <a
                                                className="table-action table-action-delete"
                                                id={tooltipIds[index]?.delete}
                                                onClick={() => handleDeleteClick(categoryData._id)}
                                            >
                                                <i className="fas fa-trash" />
                                            </a>
                                            {tooltipIds[index] && (
                                                <UncontrolledTooltip delay={0} target={tooltipIds[index].delete}>
                                                    {t('category.delete_category')}
                                                </UncontrolledTooltip>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>

                    <div className="card-footer py-4">
                        <Pagination>
                            <PaginationItem disabled={currentPage === 1}>
                                <PaginationLink previous onClick={() => paginate(currentPage - 1)} />
                            </PaginationItem>
                            {[...Array(Math.ceil(filteredCategories.length / itemsPerPage))].map((_, index) => (
                                <PaginationItem key={index} active={index + 1 === currentPage}>
                                    <PaginationLink onClick={() => paginate(index + 1)}>
                                        {index + 1}
                                    </PaginationLink>
                                </PaginationItem>
                            ))}
                            <PaginationItem disabled={currentPage === Math.ceil(filteredCategories.length / itemsPerPage)}>
                                <PaginationLink next onClick={() => paginate(currentPage + 1)} />
                            </PaginationItem>
                        </Pagination>
                    </div>
                </Card>
            </Container>

            <Modal className="modal-dialog-centered" size="sm" isOpen={formModal} toggle={toggleModal}>
                <ModalHeader toggle={toggleModal}>{t('category.edit_category')}</ModalHeader>
                <ModalBody>
                    <Card className="bg-secondary border-0 mb-0">
                        <ModalBody className="">
                            <div className="text-center text-muted mb-4">
                                <small>{t('category.edit_category_details')}</small>
                                {showSuccessAlert && (
                                    <UncontrolledAlert color="success">
                                        <span className="alert-icon">
                                            <i className="ni ni-like-2" />
                                        </span>
                                        <span className="alert-text ml-1">
                                            <strong>{t('profile.success')}</strong>{t('category.updated')}
                                        </span>
                                    </UncontrolledAlert>
                                )}
                                {showErrorAlert && (
                                    <UncontrolledAlert color="danger">
                                        <span className="alert-icon">
                                            <i className="ni ni-support-16" />
                                        </span>
                                        <span className="alert-text ml-1">
                                            <strong>{t('common.error')}</strong> {t('category.failed')}
                                        </span>
                                    </UncontrolledAlert>
                                )}
                            </div>
                            <Form onSubmit={handleSubmit}>
                                <Row>
                                    <Label className="form-control-label" htmlFor="example-email-input" md="4">
                                        Category
                                    </Label>
                                    <Col md="8">
                                        <FormGroup>
                                            <InputGroup className={classnames("input-group-merge", { focused: categoryName })}>
                                                <InputGroupAddon addonType="prepend">
                                                    <InputGroupText>
                                                        <i className="fas fa-tag" />
                                                    </InputGroupText>
                                                </InputGroupAddon>
                                                <Input
                                                    placeholder="Category name"
                                                    type="text"
                                                    value={selectedCategory.category_name || ''}
                                                    onChange={(e) => setSelectedCategory({ ...selectedCategory, category_name: e.target.value })}
                                                    required
                                                    onFocus={() => setCategoryName(true)}
                                                    onBlur={() => setCategoryName(false)}
                                                />
                                            </InputGroup>
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Button className="btn-icon" color="primary" type="submit">
                                    <span className="btn-inner--icon mr-1">
                                        <i className="ni ni-bag-17" />
                                    </span>
                                    <span className="btn-inner--text">{t('profile.submit')}</span>
                                </Button>
                                <Button color="secondary" onClick={toggleModal}>{t('profile.cancel')}</Button>
                            </Form>
                        </ModalBody>
                    </Card>
                </ModalBody>
            </Modal>
        </>
    );
}

export default Categories;
